import { z } from 'zod';
import { BrokerageAgreementSummarySchema } from './brokerage-agreements.schemas';
import { AttendeeResponseSchema } from '../communications/calendar.schemas';
import { refersTo } from '../common/refers-to';
import { InquirySourceSchema } from './inquiries.schemas';
import {
  ActivityRecordCommunicationTypeSchema,
  ActivityRecordDirectionSchema,
  ActivityRecordKindSchema,
  AppointmentTypeSchema,
} from '../activity/activity-records.schemas';
import { commaSeparated } from '../common/comma-separated';

// Domain enum tokens are en-US per ADR 0009. The German labels users see
// (Interesse, Kaufangebot, …) live in `packages/i18n`, keyed by these tokens.

// Array order IS the pipeline order (`INTEREST` first, `PURCHASED` last).
export const PROSPECT_PHASES = [
  'INTEREST',
  'WITHDRAWAL_NOTICE_CONFIRMED',
  'CONTACTED',
  'VIEWING_PROPOSED',
  'VIEWING_SCHEDULED',
  'VIEWING_COMPLETED',
  'OFFER_RECEIVED',
  'FINANCING_CONFIRMED',
  'RESERVED',
  'NOTARY_APPOINTMENT',
  'PURCHASED',
] as const;
export const PROSPECT_STATUSES = ['ACTIVE', 'WON', 'LOST'] as const;
// Array order IS the „Quelle" picker order (IMM-1098). `PORTAL` was retired by IMM-1098 and migrated to `OTHER_PORTAL`.
export const PROSPECT_SOURCES = [
  'IMMOSCOUT24',
  'IMMOWELT',
  'KLEINANZEIGEN',
  'OTHER_PORTAL',
  'WEBSITE',
  'WEB_EXPOSE',
  'SOCIAL_MEDIA',
  'PROPERTY_SIGN',
  'SHOP_WINDOW_OR_PRINT',
  'BUYER_DATABASE_OUTREACH',
  'REFERRAL',
  'TIPSTER_OR_PARTNER',
  'PHONE',
  'EMAIL',
  'OPEN_HOUSE',
  'OTHER',
] as const;
// The picker's headings, in order. Groups order the picker only and are never stored or sent.
export const PROSPECT_SOURCE_GROUPS = [
  'PORTALS',
  'OWN_CHANNELS',
  'AGENT_OUTREACH',
  'NETWORK',
  'DIRECT_CONTACT',
] as const;
export type ProspectSourceGroup = (typeof PROSPECT_SOURCE_GROUPS)[number];
export const PROSPECT_LOSS_REASONS = [
  'NO_LONGER_INTERESTED',
  'WITHDRAWAL_EXERCISED',
  'BOUGHT_ELSEWHERE',
  'FINANCING_FAILED',
  'PRICE_TOO_HIGH',
  'PROPERTY_NOT_SUITABLE',
  'SOLD_TO_OTHER',
  'UNREACHABLE',
  'OTHER',
] as const;
export const PROSPECT_PHASE_DIRECTIONS = ['FORWARD', 'BACKWARD'] as const;
export const PROSPECT_PHASE_REASON_CODES = ['VIEWING_CANCELLED', 'VIEWING_ASSESSED'] as const;

export const ProspectPhaseSchema = z.enum(PROSPECT_PHASES);
export const ProspectStatusSchema = z.enum(PROSPECT_STATUSES);
export const ProspectSourceSchema = z.enum(PROSPECT_SOURCES);
export const ProspectLossReasonSchema = z.enum(PROSPECT_LOSS_REASONS);
export const ProspectPhaseDirectionSchema = z.enum(PROSPECT_PHASE_DIRECTIONS);
export const ProspectPhaseReasonCodeSchema = z.enum(PROSPECT_PHASE_REASON_CODES);

export type ProspectPhase = z.infer<typeof ProspectPhaseSchema>;
export type ProspectPhaseReasonCode = z.infer<typeof ProspectPhaseReasonCodeSchema>;
export type ProspectStatus = z.infer<typeof ProspectStatusSchema>;
export type ProspectSource = z.infer<typeof ProspectSourceSchema>;
export type ProspectLossReason = z.infer<typeof ProspectLossReasonSchema>;
export type ProspectPhaseDirection = z.infer<typeof ProspectPhaseDirectionSchema>;

/** The heading each Quelle sits under; `OTHER` stands last without one. */
export const PROSPECT_SOURCE_GROUP = {
  IMMOSCOUT24: 'PORTALS',
  IMMOWELT: 'PORTALS',
  KLEINANZEIGEN: 'PORTALS',
  OTHER_PORTAL: 'PORTALS',
  WEBSITE: 'OWN_CHANNELS',
  WEB_EXPOSE: 'OWN_CHANNELS',
  SOCIAL_MEDIA: 'OWN_CHANNELS',
  PROPERTY_SIGN: 'OWN_CHANNELS',
  SHOP_WINDOW_OR_PRINT: 'OWN_CHANNELS',
  BUYER_DATABASE_OUTREACH: 'AGENT_OUTREACH',
  REFERRAL: 'NETWORK',
  TIPSTER_OR_PARTNER: 'NETWORK',
  PHONE: 'DIRECT_CONTACT',
  EMAIL: 'DIRECT_CONTACT',
  OPEN_HOUSE: 'DIRECT_CONTACT',
  OTHER: null,
} as const satisfies Record<ProspectSource, ProspectSourceGroup | null>;

/** The first phase that needs an offered price; every later phase needs it too. */
export const PROSPECT_OFFER_PHASE = 'OFFER_RECEIVED' satisfies ProspectPhase;
/** Entering one of these is refused while another Interessent reserves the Objekt. */
export const PROSPECT_RESERVING_PHASES = ['RESERVED', 'NOTARY_APPOINTMENT', 'PURCHASED'] as const;

export const ProspectPropertySummarySchema = z.object({
  // ACTIVE Interessenten of this Objekt, this one included when it is ACTIVE.
  activeProspectCount: z.number().int().nonnegative(),
  // The Interessent that reserves the Objekt (ACTIVE in RESERVED / NOTARY_APPOINTMENT, or WON), or null.
  reservation: z
    .object({
      prospectId: refersTo('prospect', z.uuid()),
      contactId: refersTo('contact', z.uuid()),
      // The reserving Interessent's current offer (IMM-1172)
      offeredPrice: z.number().nullable(),
    })
    .nullable(),
});

export const PROSPECT_OFFER_KINDS = ['OFFER', 'COUNTER_OFFER'] as const;
// Array order is the badge legend's order; OPEN is the status on recording.
export const PROSPECT_OFFER_STATUSES = ['OPEN', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'] as const;
// Counted in code points, as Postgres's char_length counts them.
export const PROSPECT_OFFER_NOTE_MAX_LENGTH = 500;
export const ProspectOfferKindSchema = z.enum(PROSPECT_OFFER_KINDS);
export const ProspectOfferStatusSchema = z.enum(PROSPECT_OFFER_STATUSES);
export type ProspectOfferKind = z.infer<typeof ProspectOfferKindSchema>;
export type ProspectOfferStatus = z.infer<typeof ProspectOfferStatusSchema>;

export const ProspectOfferSchema = z.object({
  id: z.uuid(),
  kind: ProspectOfferKindSchema,
  amount: z.number(),
  offeredOn: z.iso.date(),
  financingReservation: z.boolean(),
  note: z.string().nullable(),
  status: ProspectOfferStatusSchema,
  recordedAt: z.iso.datetime(),
  // null until the first status change; null on every converted entry.
  statusChangedAt: z.iso.datetime().nullable(),
});
export type ProspectOffer = z.infer<typeof ProspectOfferSchema>;

// Which of the two Besichtigungen a composer Termin is; a calendar Besichtigung's type does not say, so it has none.
export const PROSPECT_VIEWING_TYPES = ['ON_SITE', 'VIRTUAL'] as const;
export const ProspectViewingTypeSchema = z.enum(PROSPECT_VIEWING_TYPES);
export type ProspectViewingType = z.infer<typeof ProspectViewingTypeSchema>;

export const ProspectNextViewingSchema = z.object({
  appointmentId: z.uuid(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  isAllDay: z.boolean(),
  // The answer of the Interessent's Kontakt on this appointment; null when the Kontakt is not among its attendees.
  response: AttendeeResponseSchema.nullable(),
  viewingType: ProspectViewingTypeSchema.nullable(),
});
export type ProspectNextViewing = z.infer<typeof ProspectNextViewingSchema>;

export const ProspectPendingViewingAssessmentSchema = z.object({
  appointmentId: z.uuid(),
  startsAt: z.iso.datetime(),
  isAllDay: z.boolean(),
});
export type ProspectPendingViewingAssessment = z.infer<typeof ProspectPendingViewingAssessmentSchema>;

// A calendar day, no time and no zone of its own (IMM-1163). Any valid day, past included.
export const ProspectNotaryAppointmentDateSchema = z.iso.date();

export const ProspectSchema = z.object({
  id: z.uuid(),
  organizationId: refersTo('organization', z.uuid()),
  propertyId: refersTo('property', z.uuid(), { sibling: 'propertyRef' }),
  contactId: refersTo('contact', z.uuid()),
  phase: ProspectPhaseSchema,
  status: ProspectStatusSchema,
  source: ProspectSourceSchema.nullable(),
  // The current offer (IMM-1172): the ACCEPTED entry's amount; otherwise the newest OPEN entry of kind OFFER, by offeredOn then recordedAt; otherwise null. Not the stored price: a rejected offer is never the current one.
  offeredPrice: z.number().nullable(),
  // True iff offeredPrice is an ACCEPTED entry's amount (refined decision 3).
  currentOfferAccepted: z.boolean(),
  purchasePrice: z.number().nullable(),
  // The day of the Notartermin; null until one was entered. Kept when the Interessent leaves NOTARY_APPOINTMENT.
  notaryAppointmentDate: ProspectNotaryAppointmentDateSchema.nullable(),
  responsibleMemberId: refersTo('member', z.uuid()),
  // null for an Interessent an inquiry created (createdVia names the source).
  createdByActorId: refersTo('member', z.uuid()).nullable(),
  createdVia: InquirySourceSchema.nullable(),
  phaseEnteredAt: z.iso.datetime(),
  // Whole days since phaseEnteredAt: floor((now − phaseEnteredAt) / 86 400 000), computed by the handler's clock.
  daysInPhase: z.number().int().nonnegative(),
  // Every phase this Interessent has entered, in PROSPECT_PHASES order (INTEREST always). A skipped phase is absent.
  passedPhases: z.array(ProspectPhaseSchema),
  lossReason: ProspectLossReasonSchema.nullable(),
  lossNote: z.string().nullable(),
  wonAt: z.iso.datetime().nullable(),
  lostAt: z.iso.datetime().nullable(),
  property: ProspectPropertySummarySchema,
  brokerageAgreement: BrokerageAgreementSummarySchema,
  // Every offer and counter-offer, newest first by offeredOn, then recordedAt, then id.
  offers: z.array(ProspectOfferSchema),
  hasOffers: z.boolean(),
  // The earliest upcoming, not cancelled, non-private Besichtigung linked to this Interessent; null when there
  // is none and for every LOST or WON Interessent.
  nextViewing: ProspectNextViewingSchema.nullable(),
  // The oldest started, not cancelled Besichtigung of an ACTIVE Interessent that has no assessment yet; null when
  // there is none and for every LOST or WON Interessent.
  pendingViewingAssessment: ProspectPendingViewingAssessmentSchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  // The row's optimistic-concurrency counter; the MCP write results echo it.
  version: z.number().int().positive(),
});

export const ListProspectsPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

export const ListProspectsQuerySchema = z.object({
  // 'ALL' drops the status predicate (the Objekt tab needs every Interessent of one Objekt).
  status: z.enum([...PROSPECT_STATUSES, 'ALL']).default('ACTIVE'),
  propertyId: z.uuid().optional(),
  // Server-resolved from the caller's member id; never a client-supplied id.
  assignedToMe: z.stringbool().default(false),
  // Narrow to one visible Standort; intersected with the caller's set, never widening.
  organizationId: z.uuid().optional(),
  // Narrow to a set of Standorte (a node + its descendants), intersected with the caller's visible set. The web app
  // computes the subtree from the org tree it holds and sends `?organizationIds=uuid,uuid`. Takes precedence over
  // `organizationId` when non-empty; ids outside the visible set are ignored (never widens). Absent / blank → none.
  organizationIds: commaSeparated(z.uuid()),
});

export const ListProspectsResponseSchema = z.object({ data: z.array(ProspectSchema) });

/** The Objekt tab's batch bound (IMM-1363): the web app chunks its ACTIVE Interessent ids by it. */
export const LIST_PROSPECT_NEXT_ACTIVITIES_MAX_IDS = 100;

export const ListProspectNextActivitiesPathParamsSchema = z.object({ workspaceId: z.uuid() });

export const ListProspectNextActivitiesQuerySchema = z
  .object({
    prospectIds: commaSeparated(z.uuid()).refine(
      (ids) => ids.length >= 1 && ids.length <= LIST_PROSPECT_NEXT_ACTIVITIES_MAX_IDS,
    ),
  })
  .strict();

// The Deal-Überblick's next-activity shape (IMM-430.7) plus the communication's type and direction, so the cell can
// draw an Anruf as an Anruf (IMM-1363). Both are set only for a COMMUNICATION.
export const ProspectNextActivitySchema = z.object({
  activityId: z.uuid(),
  kind: ActivityRecordKindSchema,
  appointmentType: AppointmentTypeSchema.nullable(),
  communicationType: ActivityRecordCommunicationTypeSchema.nullable(),
  direction: ActivityRecordDirectionSchema.nullable(),
  content: z.string(),
  dueAt: z.iso.datetime(),
  // Derived at read time against the server's clock, never stored.
  overdue: z.boolean(),
});

export const ProspectNextActivityItemSchema = z.object({
  prospectId: refersTo('prospect', z.uuid()),
  // Null when an ACTIVE Interessent has nothing OPEN (every OPEN activity has a due date), and for every LOST or WON one.
  nextActivity: ProspectNextActivitySchema.nullable(),
});

export const ListProspectNextActivitiesResponseSchema = z.object({ data: z.array(ProspectNextActivityItemSchema) });

export const GetVisibleProspectPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const GetVisibleProspectResponseSchema = ProspectSchema;

export const CreateProspectPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const CreateProspectBodySchema = z.object({
  contactId: z.uuid(),
  propertyId: z.uuid(),
  source: ProspectSourceSchema.nullish(),
  // Absent means false, so no API caller sends by default. A key never sends, whatever it passes.
  sendBrokerageNotice: z.boolean().optional(),
});

export const CreateProspectResponseSchema = ProspectSchema;

// Not refined to > 0: the aggregate refuses <= 0 with the gate's own code, which a Zod 400 would replace.
// The upper bound is numeric(14,2)'s.
const ProspectPriceSchema = z.number().max(999_999_999_999.99);

export const AdvanceProspectPhasePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const AdvanceProspectPhaseBodySchema = z.object({
  toPhase: ProspectPhaseSchema,
  offeredPrice: ProspectPriceSchema.nullable().optional(),
  // Read only together with offeredPrice: the offer of the buyer the move records. offerDate is YYYY-MM-DD, not after
  // today in Europe/Berlin, default today; offerFinancingReservation defaults to false; offerNote is at most 500
  // characters. Not refined here: the aggregate refuses with the offer's own codes, which a Zod 400 would replace.
  offerDate: z.iso.date().nullable().optional(),
  offerFinancingReservation: z.boolean().optional(),
  offerNote: z.string().nullable().optional(),
  purchasePrice: ProspectPriceSchema.nullable().optional(),
  // Required (on the request, a stored date does not count) when toPhase is NOTARY_APPOINTMENT; refused with any
  // other toPhase. Not refined here: the aggregate refuses with the gate's own code, which a Zod 400 would replace.
  notaryAppointmentDate: ProspectNotaryAppointmentDateSchema.nullable().optional(),
});

export const AdvanceProspectPhaseResponseSchema = ProspectSchema;

const ProspectWritePathParamsSchema = z.object({
  organizationId: refersTo('organization', z.uuid()),
  id: z.uuid(),
});

export const RevertProspectPhasePathParamsSchema = ProspectWritePathParamsSchema;

// `reason` is not refined to be non-empty: the aggregate refuses a blank one with the PRD's copy, so the API and
// the client tool get the same message.
export const RevertProspectPhaseBodySchema = z.object({
  toPhase: ProspectPhaseSchema,
  reason: z.string().trim().max(1000),
});

export const RevertProspectPhaseResponseSchema = ProspectSchema;

export const RecordProspectOfferPathParamsSchema = ProspectWritePathParamsSchema;

// Not refined: amount <= 0, a future offeredOn, a long note and a counter-offer's reservation are refused by the
// aggregate with their own codes, which a Zod 400 would replace (the app and the client tool show the same refusal).
export const RecordProspectOfferBodySchema = z.object({
  kind: ProspectOfferKindSchema,
  amount: ProspectPriceSchema,
  // Absent means today in Europe/Berlin.
  offeredOn: z.iso.date().optional(),
  // OFFER only; absent means false.
  financingReservation: z.boolean().optional(),
  note: z.string().nullable().optional(),
});

export const RecordProspectOfferResponseSchema = ProspectSchema;

export const ChangeProspectOfferStatusPathParamsSchema = z.object({
  organizationId: refersTo('organization', z.uuid()),
  id: z.uuid(),
  offerId: z.uuid(),
});

export const ChangeProspectOfferStatusBodySchema = z.object({
  // OPEN is „Wieder öffnen". Which moves are allowed is the aggregate's rule, refused with its own code.
  toStatus: ProspectOfferStatusSchema,
});

export const ChangeProspectOfferStatusResponseSchema = ProspectSchema;

export const UpdateProspectNotaryAppointmentDatePathParamsSchema = ProspectWritePathParamsSchema;

// Not nullable: a date once set cannot be cleared (IMM-1163, Detailed Business Rule 4).
export const UpdateProspectNotaryAppointmentDateBodySchema = z.object({
  notaryAppointmentDate: ProspectNotaryAppointmentDateSchema,
});

export const UpdateProspectNotaryAppointmentDateResponseSchema = ProspectSchema;

export const MarkProspectLostPathParamsSchema = ProspectWritePathParamsSchema;

export const MarkProspectLostBodySchema = z.object({
  lossReason: ProspectLossReasonSchema,
  lossNote: z.string().trim().max(1000).nullable().optional(),
});

export const MarkProspectLostResponseSchema = ProspectSchema;

export const ReopenProspectPathParamsSchema = ProspectWritePathParamsSchema;

export const ReopenProspectBodySchema = z.object({}).strict();

export const ReopenProspectResponseSchema = ProspectSchema;

export const SendBrokerageNoticeResponseSchema = ProspectSchema;

export const MarkBrokerageAcceptanceReplyResponseSchema = ProspectSchema;

export type Prospect = z.infer<typeof ProspectSchema>;
export type ProspectPropertySummary = z.infer<typeof ProspectPropertySummarySchema>;
export type ListProspectsPathParams = z.infer<typeof ListProspectsPathParamsSchema>;
export type ListProspectsQuery = z.infer<typeof ListProspectsQuerySchema>;
export type ListProspectsResponse = z.infer<typeof ListProspectsResponseSchema>;
export type ProspectNextActivity = z.infer<typeof ProspectNextActivitySchema>;
export type ProspectNextActivityItem = z.infer<typeof ProspectNextActivityItemSchema>;
export type ListProspectNextActivitiesResponse = z.infer<typeof ListProspectNextActivitiesResponseSchema>;
export type GetVisibleProspectPathParams = z.infer<typeof GetVisibleProspectPathParamsSchema>;
export type GetVisibleProspectResponse = z.infer<typeof GetVisibleProspectResponseSchema>;
export type CreateProspectPathParams = z.infer<typeof CreateProspectPathParamsSchema>;
export type CreateProspectBody = z.infer<typeof CreateProspectBodySchema>;
export type AdvanceProspectPhasePathParams = z.infer<typeof AdvanceProspectPhasePathParamsSchema>;
export type AdvanceProspectPhaseBody = z.infer<typeof AdvanceProspectPhaseBodySchema>;
export type AdvanceProspectPhaseResponse = z.infer<typeof AdvanceProspectPhaseResponseSchema>;
export type RevertProspectPhasePathParams = z.infer<typeof RevertProspectPhasePathParamsSchema>;
export type RevertProspectPhaseBody = z.infer<typeof RevertProspectPhaseBodySchema>;
export type RevertProspectPhaseResponse = z.infer<typeof RevertProspectPhaseResponseSchema>;
export type RecordProspectOfferPathParams = z.infer<typeof RecordProspectOfferPathParamsSchema>;
export type RecordProspectOfferBody = z.infer<typeof RecordProspectOfferBodySchema>;
export type RecordProspectOfferResponse = z.infer<typeof RecordProspectOfferResponseSchema>;
export type ChangeProspectOfferStatusPathParams = z.infer<typeof ChangeProspectOfferStatusPathParamsSchema>;
export type ChangeProspectOfferStatusBody = z.infer<typeof ChangeProspectOfferStatusBodySchema>;
export type ChangeProspectOfferStatusResponse = z.infer<typeof ChangeProspectOfferStatusResponseSchema>;
export type UpdateProspectNotaryAppointmentDatePathParams = z.infer<
  typeof UpdateProspectNotaryAppointmentDatePathParamsSchema
>;
export type UpdateProspectNotaryAppointmentDateBody = z.infer<typeof UpdateProspectNotaryAppointmentDateBodySchema>;
export type UpdateProspectNotaryAppointmentDateResponse = z.infer<
  typeof UpdateProspectNotaryAppointmentDateResponseSchema
>;
export type MarkProspectLostPathParams = z.infer<typeof MarkProspectLostPathParamsSchema>;
export type MarkProspectLostBody = z.infer<typeof MarkProspectLostBodySchema>;
export type MarkProspectLostResponse = z.infer<typeof MarkProspectLostResponseSchema>;
export type ReopenProspectPathParams = z.infer<typeof ReopenProspectPathParamsSchema>;
export type ReopenProspectBody = z.infer<typeof ReopenProspectBodySchema>;
export type ReopenProspectResponse = z.infer<typeof ReopenProspectResponseSchema>;
export type SendBrokerageNoticeResponse = z.infer<typeof SendBrokerageNoticeResponseSchema>;
export type MarkBrokerageAcceptanceReplyResponse = z.infer<typeof MarkBrokerageAcceptanceReplyResponseSchema>;
